import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./ServiceDebts.module.scss";
import { ServiceDebtService } from "../../../services/serviceDebt.service";
import { RootState, AppDispatch } from "../../../redux/store";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";
import {
  ServiceDebtItem,
  ServiceDebtSummary,
} from "../../../interface/site.interface";
import Spinner from "../../../components/shared/spinner/Spinner";
import MainButton from "../../../components/shared/button/MainButton";
import ServiceDebtPayModal from "../../../components/shared/service-debt-pay-modal/ServiceDebtPayModal";

type FilterType = "all" | "unpaid" | "paid";

const ServiceDebts = () => {
  const { data: t } = useSelector((state: RootState) => state.translationData);
  const language = useSelector((state: RootState) => state.language.language);
  const dispatch = useDispatch<AppDispatch>();
  const initialRender = useRef(true);

  const [debts, setDebts] = useState<ServiceDebtItem[]>([]);
  const [summary, setSummary] = useState<ServiceDebtSummary>({ count: 0, total_azn: 0 });
  const [filter, setFilter] = useState<FilterType>("unpaid");
  const [loading, setLoading] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [payModalOpen, setPayModalOpen] = useState(false);

  const fetchSummary = () => {
    ServiceDebtService.getSummary()
      .then((res) => setSummary(res.data))
      .catch(() => {});
  };

  const fetchDebts = (url?: string) => {
    const isInitial = !url;
    isInitial ? setLoading(true) : setLoadingMore(true);
    ServiceDebtService.getDebts(filter, url)
      .then((res) => {
        const resp = res.data;
        if (isInitial) {
          setDebts(resp.data || []);
        } else {
          setDebts((prev) => [...prev, ...(resp.data || [])]);
        }
        setNextPageUrl(resp.next_page_url || null);
      })
      .catch(() => {})
      .finally(() => (isInitial ? setLoading(false) : setLoadingMore(false)));
  };

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!t) dispatch(fetchTranslation());
    } else {
      dispatch(fetchTranslation());
    }
  }, [language]);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    fetchDebts();
  }, [filter]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllUnpaid = () => {
    const unpaidIds = debts.filter((d) => d.paid_status === "unpaid").map((d) => d.id);
    setSelectedIds(unpaidIds);
  };

  const effectiveDebtIds = selectedIds.length > 0 ? selectedIds : undefined;
  const selectedDebts = selectedIds.length > 0
    ? debts.filter((d) => selectedIds.includes(d.id))
    : debts.filter((d) => d.paid_status === "unpaid");
  const selectedTotal = selectedDebts.reduce((acc, d) => acc + Number(d.amount), 0);

  const afterPaySuccess = () => {
    fetchSummary();
    fetchDebts();
    setSelectedIds([]);
  };

  const debtTypeLabel = (type: string): string => {
    const key = `debt_type_${type.toLowerCase()}` as keyof typeof t;
    return (t && t[key] as string) || type;
  };

  const filterLabel: Record<FilterType, string> = {
    unpaid: t?.service_debts_filter_unpaid || "Ödənilməmiş",
    paid:   t?.service_debts_filter_paid   || "Ödənilmiş",
    all:    t?.service_debts_filter_all    || "Hamısı",
  };

  const unpaidCount = summary.count;

  return (
    <div id={styles.service_debts}>
      {unpaidCount > 0 && (
        <div className={styles.summary_card}>
          <div className={styles.summary_info}>
            <h3>{t?.service_debts_unpaid_label || "Ödənilməmiş xidmət borcu"}</h3>
            <div className={styles.total}>{summary.total_azn.toFixed(2)} AZN</div>
            <div className={styles.count}>{unpaidCount} {t?.service_debts_count || "borc"}</div>
          </div>
          <MainButton
            className={styles.pay_all_btn}
            text={t?.service_debts_pay_all || "Hamısını ödə"}
            onClick={() => {
              setSelectedIds([]);
              setPayModalOpen(true);
            }}
          />
        </div>
      )}

      <div className={styles.filter_tabs}>
        {(["unpaid", "paid", "all"] as FilterType[]).map((f) => (
          <button
            key={f}
            className={filter === f ? styles.active : ""}
            onClick={() => setFilter(f)}
          >
            {filterLabel[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner size="large" />
      ) : debts.length === 0 ? (
        <p className={styles.no_data}>
          {filter === "unpaid"
            ? t?.service_debts_no_unpaid || "Ödənilməmiş borc yoxdur"
            : t?.service_debts_no_data   || "Məlumat yoxdur"}
        </p>
      ) : (
        <>
          <div className={styles.debt_list}>
            {debts.map((debt) => (
              <div
                key={debt.id}
                className={`${styles.debt_card} ${
                  selectedIds.includes(debt.id) ? styles.selected : ""
                }`}
              >
                {debt.paid_status === "unpaid" && (
                  <div className={styles.checkbox_wrap}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(debt.id)}
                      onChange={() => toggleSelect(debt.id)}
                    />
                  </div>
                )}
                <div className={styles.debt_info}>
                  {debt.internal_id && (
                    <div className={styles.debt_package}>{debt.internal_id}</div>
                  )}
                  <div className={styles.debt_type}>{debtTypeLabel(debt.type)}</div>
                  {debt.reason && (
                    <div className={styles.debt_reason}>{debt.reason}</div>
                  )}
                  <div className={styles.debt_date}>
                    {new Date(debt.created_at).toLocaleDateString("az-AZ")}
                  </div>
                </div>
                <div className={styles.debt_amount}>{Number(debt.amount).toFixed(2)} AZN</div>
                <div className={`${styles.debt_status} ${styles[debt.paid_status]}`}>
                  {debt.paid_status === "unpaid"
                    ? t?.service_debts_filter_unpaid || "Ödənilməmiş"
                    : t?.service_debts_filter_paid   || "Ödənilmiş"}
                </div>
              </div>
            ))}
          </div>

          {nextPageUrl && (
            <div className={styles.pagination}>
              <button
                disabled={loadingMore}
                onClick={() => fetchDebts(nextPageUrl)}
              >
                {loadingMore
                  ? t?.service_debts_loading  || "Yüklənir..."
                  : t?.service_debts_load_more || "Daha çox"}
              </button>
            </div>
          )}

          {filter === "unpaid" && debts.some((d) => d.paid_status === "unpaid") && (
            <div className={styles.action_bar}>
              <span className={styles.selected_info}>
                {selectedIds.length > 0
                  ? `${selectedIds.length} ${t?.service_debts_selected || "borc seçilib"} — ${selectedTotal.toFixed(2)} AZN`
                  : `${t?.service_debts_total || "Cəmi"}: ${summary.total_azn.toFixed(2)} AZN`}
              </span>
              {selectedIds.length === 0 && (
                <MainButton
                  text={t?.service_debts_select_all || "Hamısını seç"}
                  onClick={selectAllUnpaid}
                />
              )}
              <MainButton
                text={t?.pay || "Ödə"}
                onClick={() => setPayModalOpen(true)}
                disabled={selectedIds.length === 0 && summary.count === 0}
              />
            </div>
          )}
        </>
      )}

      <ServiceDebtPayModal
        isOpen={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        amountAzn={selectedIds.length > 0 ? selectedTotal : summary.total_azn}
        debtIds={effectiveDebtIds}
        onSuccess={afterPaySuccess}
      />
    </div>
  );
};

export default ServiceDebts;
